// Requêtes de la rubrique « Paiements » du portail (TanStack Query, clés sous « citoyen »).
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../../api/client';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import type { Liste } from '../types';
import type { AttributionUsager, CreanceUsager, PageSimulateur, PaiementInitie, PaiementUsager, QuittanceUsager, ResultatSimulateur } from './types';

export const CLES_PAIEMENTS = {
  creances: ['citoyen', 'creances'] as const,
  paiements: ['citoyen', 'paiements'] as const,
  quittances: ['citoyen', 'quittances'] as const,
  attributions: ['citoyen', 'attributions'] as const,
  simulateur: (reference: string) => ['citoyen', 'simulateur', reference] as const,
};

function useConnecte() {
  return useSessionUsager().etat === 'connecte';
}

export function useMesCreances() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES_PAIEMENTS.creances,
    queryFn: ({ signal }) => requete<Liste<CreanceUsager>>('/usager/creances', { signal }).then((r) => r.elements),
    enabled: connecte,
    retry: false,
  });
}

/** Paiements de l'usager ; relus toutes les cinq secondes tant qu'un paiement attend le prestataire. */
export function useMesPaiements(suivre = false) {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES_PAIEMENTS.paiements,
    queryFn: ({ signal }) => requete<Liste<PaiementUsager>>('/usager/paiements', { signal }).then((r) => r.elements),
    enabled: connecte,
    retry: false,
    refetchInterval: suivre ? 5_000 : false,
  });
}

export function useMesQuittances(suivre = false) {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES_PAIEMENTS.quittances,
    queryFn: ({ signal }) => requete<Liste<QuittanceUsager>>('/usager/quittances', { signal }).then((r) => r.elements),
    enabled: connecte,
    retry: false,
    refetchInterval: suivre ? 5_000 : false,
  });
}

export function useMesAttributions() {
  const connecte = useConnecte();
  return useQuery({
    queryKey: CLES_PAIEMENTS.attributions,
    queryFn: ({ signal }) => requete<Liste<AttributionUsager>>('/usager/attributions', { signal }).then((r) => r.elements),
    enabled: connecte,
    retry: false,
  });
}

/** Page du simulateur de paiement : publique, sans jeton ; 404 quand aucun simulateur n'est configuré. */
export function usePageSimulateur(reference: string | null) {
  return useQuery({
    queryKey: CLES_PAIEMENTS.simulateur(reference ?? ''),
    queryFn: ({ signal }) => requete<PageSimulateur>(`/public/simulateur-paiement/${encodeURIComponent(reference as string)}`, { signal }),
    enabled: Boolean(reference),
    retry: false,
    staleTime: 0,
  });
}

export const payerCreance = (id: string, corps: { montant?: number }) => requete<PaiementInitie>(`/usager/creances/${id}/payer`, { methode: 'POST', corps });

export const agirSimulateur = (reference: string, action: string) =>
  requete<ResultatSimulateur>(`/public/simulateur-paiement/${encodeURIComponent(reference)}/${action}`, { methode: 'POST', corps: {} });

/** Télécharge le PDF d'une quittance (route authentifiée qui renvoie le fichier) et le propose à l'enregistrement. */
export async function telechargerQuittance(id: string, numero: string): Promise<void> {
  const r = await requete<Response>(`/usager/quittances/${id}/pdf`, { brut: true });
  const url = URL.createObjectURL(await r.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = `quittance-${numero}.pdf`;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
